'use client';

import React, { ReactElement } from 'react';
import { useTranslations } from 'next-intl';
import { RichTextFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import QuantitySelector from '@/ui/components/QuantitySelector/QuantitySelector';
import { AttributeTilePicker } from '@/ui/components/pages/ProductDetail/attribute-tile-picker/attribute-tile-picker';
import { ColorPicker } from '@/ui/components/pages/ProductDetail/color-picker/color-picker';
import ShippingAvailabilityModal from '@/ui/components/shared/ShippingAvailabilityModal';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { ClassicPdpUsp } from '../classic-pdp-content';
import {
  CLASSIC_PDP_MAX_QUANTITY,
  CLASSIC_PDP_MIN_QUANTITY,
  ClassicPdpSelectionState,
} from '../use-classic-pdp-selection';
import { AddToCartButton } from './add-to-cart-button';
import { ProductPrice } from './product-price';
import { ProductUsps } from './product-usps';
import { ReviewRatingSlot } from './review-rating-slot';
import { UnavailableNotice } from './unavailable-notice';

export interface PurchasePanelProps {
  title: string;
  subtitle?: string;
  shortDescription?: RichTextFragment;
  usps: ClassicPdpUsp[];
  price: string;
  isVatNoteHidden: boolean;
  isRatingVisible: boolean;
  colorHeading?: RichTextFragment;
  cylinderHeading?: RichTextFragment;
  wildcardHeading?: RichTextFragment;
  selection: ClassicPdpSelectionState;
  /**
   * Shown instead of the quantity and "Add to cart", e.g. the Nuki Club buttons. An element, not
   * any node, so a `condition && <X />` (false) cannot hide the purchase row by accident.
   */
  purchaseActions?: ReactElement;
  isAddToCartDisabled?: boolean;
}

export function PurchasePanel({
  title,
  subtitle,
  shortDescription,
  usps,
  price,
  isVatNoteHidden,
  isRatingVisible,
  colorHeading,
  cylinderHeading,
  wildcardHeading,
  selection,
  purchaseActions,
  isAddToCartDisabled = false,
}: Readonly<PurchasePanelProps>) {
  const t = useTranslations('ClassicProductDetail');
  const tQuantity = useTranslations('ProductQuantityDisplay');

  const {
    currentVariant,
    visibleColors,
    visibleCylinders,
    visibleWildcards,
    quantity,
    isAvailable,
    isShippingModalOpen,
    externalAreaLink,
    setQuantity,
    selectVariant,
    addToCart,
    notifyMe,
    closeShippingModal,
    proceedDespiteShippingWarning,
  } = selection;

  return (
    <div data-testid="classic-pdp-purchase-panel" className="flex flex-col gap-6">
      {isRatingVisible && <ReviewRatingSlot />}

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-neutral-100">{title}</h1>
        {subtitle && <p className="mt-2 text-xl text-neutral-55">{subtitle}</p>}
      </div>

      {shortDescription && (
        <RichTextRenderer className="text-base text-neutral-55" blocks={shortDescription} />
      )}

      <ProductPrice price={price} isVatNoteHidden={isVatNoteHidden} />

      {visibleColors.length > 0 && (
        <ColorPicker
          sectionHeading={colorHeading}
          availableColors={visibleColors}
          selectedColor={currentVariant.color}
          setSelectedColor={(color) => selectVariant({ color: color.name })}
          variant="classic"
        />
      )}

      <AttributeTilePicker
        sectionHeading={cylinderHeading}
        options={visibleCylinders}
        selected={currentVariant.cylinder}
        onSelect={(cylinder) => selectVariant({ cylinder: cylinder.type })}
        variant="classic"
      />

      <AttributeTilePicker
        sectionHeading={wildcardHeading}
        options={visibleWildcards}
        selected={currentVariant.wildcard}
        onSelect={(wildcard) => selectVariant({ wildcard: wildcard.type })}
        variant="classic"
      />

      <div className="flex flex-col gap-3">
        {!isAvailable && <UnavailableNotice />}

        {purchaseActions ?? (
          // The stepper has no visible caption, so it carries its accessible names itself.
          <div data-testid="classic-pdp-purchase-row" className="flex items-center gap-3">
            {/* Remounting on a new SKU resets the stepper to its default. */}
            <QuantitySelector
              key={currentVariant.sku}
              variant="pill"
              defaultQuantity={quantity}
              minQuantity={CLASSIC_PDP_MIN_QUANTITY}
              maxQuantity={CLASSIC_PDP_MAX_QUANTITY}
              onQuantityChange={setQuantity}
              isEditable
              decreaseLabel={tQuantity('decreaseQuantity')}
              increaseLabel={tQuantity('increaseQuantity')}
              valueLabel={t('quantity')}
            />
            <AddToCartButton
              className="flex-1"
              isAvailable={isAvailable}
              onAddToCart={addToCart}
              onNotify={notifyMe}
              disabled={isAddToCartDisabled}
            />
          </div>
        )}

        <ProductUsps usps={usps} />
      </div>

      {externalAreaLink && (
        <ShippingAvailabilityModal
          open={isShippingModalOpen}
          onClose={closeShippingModal}
          onTryAnyway={proceedDespiteShippingWarning}
          externalLink={externalAreaLink}
        />
      )}
    </div>
  );
}
